import { ROW_BREAK } from "../../../../../../../../shared/frontend/dashboard/dashboardOrder";
import type { DashboardWidget } from "../../../../../../../../shared/frontend/dashboard/useDashboardLayout";

/**
 * Blöcke der Übersicht in der Personenakte (Plan `plans/2026-10-22-personenakte-uebersicht-baukasten.md`).
 * Dasselbe Raster wie das Dashboard: Karten einer Zeile teilen sich die Breite, ein fehlender Block lässt
 * die Zeile zusammenrücken. Ausgeblendet ist ein Block nur durch den Benutzer oder fehlendes Recht —
 * fehlende Daten zeigen den Leerzustand des Blocks.
 */
export const PERSON_OVERVIEW_WIDGET_KEYS = [
  "situation",
  "todos",
  "checkins7",
  "checkins30",
  "checkinsPerWeek",
  "checkinsTotal",
  "attendanceTrend",
  "membership",
  "checkinSessions",
  "payments",
  "notes",
  "accessMedia",
  "memberCare",
  "readerActions",
  "openItems",
  "timeline"
] as const;

export type PersonOverviewWidgetKey = (typeof PERSON_OVERVIEW_WIDGET_KEYS)[number];

/** Gespeicherter Zustand je Block; `limit` nur bei Listen (Anzahl Zeilen). */
export type PersonOverviewWidgetState = DashboardWidget & { limit?: number };

/** Standardanordnung; `|` beginnt eine neue Zeile. Optionale Blöcke hängt das Raster ausgeblendet an. */
export const PERSON_OVERVIEW_DEFAULT_ORDER: string[] = [
  "situation", ROW_BREAK,
  // Was zu tun ist und was das Team notiert hat, gehört nebeneinander nach oben.
  "todos", "notes", ROW_BREAK,
  "attendanceTrend", "membership", ROW_BREAK,
  // Tabellen in voller Breite, damit alle Spalten Platz haben.
  "checkinSessions", ROW_BREAK,
  "payments"
];

/** Standardmäßig ausgeblendet, über das Zahnrad einschaltbar. */
export const PERSON_OVERVIEW_OPTIONAL_WIDGETS: ReadonlySet<PersonOverviewWidgetKey> = new Set<PersonOverviewWidgetKey>([
  // Einzelkacheln der Anwesenheit: die Werte stehen im Block „Anwesenheit“, die Kacheln sind Zusatz.
  "checkins7",
  "checkins30",
  "checkinsPerWeek",
  "checkinsTotal",
  "accessMedia",
  "memberCare",
  "readerActions",
  "openItems",
  "timeline"
]);

/** Listenblöcke: Zeilen teilen sich die Resthöhe wie im Dashboard. */
export const PERSON_OVERVIEW_LIST_WIDGETS: ReadonlySet<string> = new Set<PersonOverviewWidgetKey>([
  "checkinSessions",
  "payments",
  "openItems",
  "timeline",
  "readerActions",
  "memberCare"
]);

/** Kurze Listen mit wählbarer Zeilenanzahl (im Layoutmodus); die Tabellen blättern selbst. */
export const PERSON_OVERVIEW_LIMIT_WIDGETS: ReadonlySet<string> = new Set<PersonOverviewWidgetKey>([
  "openItems",
  "timeline"
]);

export const PERSON_OVERVIEW_LIMIT_OPTIONS = [3, 5, 10, 20] as const;
export const PERSON_OVERVIEW_DEFAULT_LIMIT = 5;

/** Tabellen der Übersicht: Startseitengröße und wählbare Größen (je Benutzer gespeichert). */
export const PERSON_OVERVIEW_TABLE_PAGE_SIZE = 5;
export const PERSON_OVERVIEW_TABLE_PAGE_SIZE_OPTIONS: readonly number[] = [5, 10, 20, 50];

/** Gespeicherte Einstellung mit dem Katalog zusammenführen: unbekannte Blöcke fallen weg, neue bekommen ihren Standard. */
export function resolvePersonOverviewWidgets(saved: readonly PersonOverviewWidgetState[] | undefined): PersonOverviewWidgetState[] {
  return PERSON_OVERVIEW_WIDGET_KEYS.map((key) => {
    const stored = saved?.find((widget) => widget.key === key);
    const limit = PERSON_OVERVIEW_LIMIT_WIDGETS.has(key)
      ? (PERSON_OVERVIEW_LIMIT_OPTIONS as readonly number[]).includes(Number(stored?.limit)) ? Number(stored?.limit) : PERSON_OVERVIEW_DEFAULT_LIMIT
      : undefined;
    return { key, visible: stored ? stored.visible !== false : !PERSON_OVERVIEW_OPTIONAL_WIDGETS.has(key), ...(limit != null ? { limit } : {}) };
  });
}
